import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('profile');

  const [profileData, setProfileData] = useState({
    name: 'Administrator',
    email: 'admin@dawaak.com',
    phone: '+966 50 123 4567',
    timezone: 'Asia/Riyadh'
  });

  const [systemSettings, setSystemSettings] = useState({
    language: i18n.language,
    theme: 'light',
    notifications: true,
    emailAlerts: true,
    autoSave: true
  });

  const handleProfileSave = () => {
    addToast({ title: t('settings.profileUpdated'), message: '', type: 'success' });
  };

  const handleSystemSave = () => {
    if (systemSettings.language !== i18n.language) {
      i18n.changeLanguage(systemSettings.language);
      document.documentElement.dir = systemSettings.language === 'ar' ? 'rtl' : 'ltr';
    }
    addToast({ title: t('settings.systemUpdated'), message: '', type: 'success' });
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile') },
    { id: 'system', label: t('settings.system') },
    { id: 'security', label: t('settings.security') },
    { id: 'notifications', label: t('settings.notifications') }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">{t('navigation.settings')}</h1>
      </div>

      <div className="page-content">
        <div className="settings-container">
          <div className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="settings-content">
            {activeTab === 'profile' && (
              <Card>
                <div className="settings-section">
                  <h2 className="section-title">{t('settings.profileSettings')}</h2>
                  <p className="section-description">{t('settings.profileDescription')}</p>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('settings.fullName')}</label>
                      <Input
                        value={profileData.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setProfileData(prev => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('settings.emailAddress')}</label>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setProfileData(prev => ({ ...prev, email: e.target.value }))
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('settings.phoneNumber')}</label>
                      <Input
                        value={profileData.phone}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setProfileData(prev => ({ ...prev, phone: e.target.value }))
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('settings.timezone')}</label>
                      <select
                        value={profileData.timezone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                        className="form-select"
                      >
                        <option value="Asia/Riyadh">Riyadh (GMT+3)</option>
                        <option value="Europe/London">London (GMT+0)</option>
                        <option value="America/New_York">New York (GMT-5)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-actions">
                    <Button variant="primary" onClick={handleProfileSave}>
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'system' && (
              <Card>
                <div className="settings-section">
                  <h2 className="section-title">{t('settings.systemSettings')}</h2>
                  <p className="section-description">{t('settings.systemDescription')}</p>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('settings.language')}</label>
                      <select
                        value={systemSettings.language}
                        onChange={(e) => setSystemSettings(prev => ({ ...prev, language: e.target.value }))}
                        className="form-select"
                      >
                        <option value="en">English</option>
                        <option value="ar">العربية</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t('settings.theme')}</label>
                      <select
                        value={systemSettings.theme}
                        onChange={(e) => setSystemSettings(prev => ({ ...prev, theme: e.target.value }))}
                        className="form-select"
                      >
                        <option value="light">{t('settings.lightTheme')}</option>
                        <option value="dark">{t('settings.darkTheme')}</option>
                        <option value="auto">{t('settings.autoTheme')}</option>
                      </select>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={systemSettings.notifications}
                          onChange={(e) => setSystemSettings(prev => ({ ...prev, notifications: e.target.checked }))}
                        />
                        <span>{t('settings.enableNotifications')}</span>
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={systemSettings.emailAlerts}
                          onChange={(e) => setSystemSettings(prev => ({ ...prev, emailAlerts: e.target.checked }))}
                        />
                        <span>{t('settings.emailAlerts')}</span>
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={systemSettings.autoSave}
                          onChange={(e) => setSystemSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
                        />
                        <span>{t('settings.autoSave')}</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-actions">
                    <Button variant="primary" onClick={handleSystemSave}>
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'security' && (
              <Card>
                <div className="settings-section">
                  <h2 className="section-title">{t('settings.securitySettings')}</h2>
                  <p className="section-description">{t('settings.securityDescription')}</p>

                  <div className="security-item">
                    <div className="security-info">
                      <h3>{t('settings.changePassword')}</h3>
                      <p>{t('settings.passwordDescription')}</p>
                    </div>
                    <Button variant="secondary">
                      {t('settings.updatePassword')}
                    </Button>
                  </div>

                  <div className="security-item">
                    <div className="security-info">
                      <h3>{t('settings.twoFactorAuth')}</h3>
                      <p>{t('settings.twoFactorDescription')}</p>
                    </div>
                    <Button variant="primary">
                      {t('settings.enable')}
                    </Button>
                  </div>

                  <div className="security-item">
                    <div className="security-info">
                      <h3>{t('settings.loginSessions')}</h3>
                      <p>{t('settings.sessionsDescription')}</p>
                    </div>
                    <Button variant="secondary">
                      {t('settings.viewSessions')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'notifications' && (
              <Card>
                <div className="settings-section">
                  <h2 className="section-title">{t('settings.notificationSettings')}</h2>
                  <p className="section-description">{t('settings.notificationDescription')}</p>

                  <div className="notification-groups">
                    <div className="notification-group">
                      <h3>{t('settings.orderNotifications')}</h3>
                      <div className="notification-options">
                        <label className="checkbox-label">
                          <input type="checkbox" defaultChecked />
                          <span>{t('settings.newOrders')}</span>
                        </label>
                        <label className="checkbox-label">
                          <input type="checkbox" defaultChecked />
                          <span>{t('settings.orderUpdates')}</span>
                        </label>
                      </div>
                    </div>

                    <div className="notification-group">
                      <h3>{t('settings.systemNotifications')}</h3>
                      <div className="notification-options">
                        <label className="checkbox-label">
                          <input type="checkbox" defaultChecked />
                          <span>{t('settings.systemUpdates')}</span>
                        </label>
                        <label className="checkbox-label">
                          <input type="checkbox" defaultChecked />
                          <span>{t('settings.securityAlerts')}</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <Button variant="primary">
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;